import * as types from "./actionTypes";
import * as libraryApi from "../api/libraryApi";
import { failureMessage, isForbidden, isNotFound } from "../api/client";
import { showNotice } from "./noticeActions";
import { loadFavoritesCache, saveFavoritesCache } from "../offline/favoritesCache";

let loadSequence = 0;

export const updateLibrarySearch = (searchValue) => ({ type: types.LIBRARY_SEARCH_CHANGE, searchValue });

/**
 * Loads the favourites of the session's host. With "Keep favorites offline" on, the list is
 * kept on the device and shown from there when the server cannot be reached.
 */
export const loadLibrary = (code) => async (dispatch, getState) => {
  if (!code) return;
  const sequence = ++loadSequence;
  const keepOffline = () => getState().preferences.keepFavoritesOffline;
  try {
    const songs = await libraryApi.getLibrarySongs(code);
    if (sequence !== loadSequence) return;
    dispatch({ type: types.LIBRARY_LOAD_SUCCESS, songs });
    if (keepOffline()) saveFavoritesCache(code, songs);
  } catch (error) {
    if (sequence !== loadSequence) return;
    const cached = keepOffline() ? loadFavoritesCache(code) : null;
    if (cached && !error.response) {
      dispatch({ type: types.LIBRARY_LOAD_SUCCESS, songs: cached, fromCache: true });
      return;
    }
    console.error("Could not load the favourites", error);
    dispatch({ type: types.LIBRARY_LOAD_FAILED });
  }
};

// pushed by the server (and applied locally from the REST response)
export const librarySongAdded = (song) => ({ type: types.LIBRARY_SONG_ADDED, song });
export const librarySongRemoved = (songId) => ({ type: types.LIBRARY_SONG_REMOVED, songId });

const failureNotice = (error, what) => {
  if (isForbidden(error)) return "Only a session host can change the favourites.";
  if (isNotFound(error)) return "That song is no longer available.";
  return failureMessage(error, `Couldn't ${what}. Check your connection and try again.`);
};

/** Adds a song to the favourites (host only). */
export const addSongToLibrary = (songId) => async (dispatch) => {
  try {
    const song = await libraryApi.addSong(songId);
    dispatch(librarySongAdded(song));
  } catch (error) {
    dispatch(showNotice(failureNotice(error, "add the song to the favourites")));
  }
};

/** Removes a song from the favourites (host only). */
export const removeSongFromLibrary = (songId) => async (dispatch) => {
  try {
    await libraryApi.removeSong(songId);
  } catch (error) {
    if (!isNotFound(error)) {
      dispatch(showNotice(failureNotice(error, "remove the song from the favourites")));
      return;
    }
  }
  dispatch(librarySongRemoved(songId));
};
